import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { homePage } from "@/mocks/home";

const heroImages = [
  {
    src: "https://readdy.ai/api/search-image?query=freshly%20baked%20Japanese%20taiyaki%20red%20bean%20cakes%20on%20traditional%20griddle%20golden%20brown%20crispy%20exterior%20warm%20steam%20rising%20artisan%20street%20food%20stall%20warm%20amber%20bokeh%20lighting%20cozy%20Taiwanese%20night%20market%20atmosphere%20food%20photography%20shallow%20depth%20of%20field%20warm%20tones%20appetizing&width=1600&height=900&seq=home-hero-1&orientation=landscape",
    alt: "來加綉綉現烤紅豆餅出爐畫面",
  },
  {
    src: "https://readdy.ai/api/search-image?query=artisan%20hands%20shaping%20taiyaki%20red%20bean%20cake%20dough%20on%20wooden%20board%20flour%20dusting%20warm%20golden%20workshop%20lighting%20cozy%20handmade%20atmosphere%20shallow%20depth%20of%20field%20documentary%20food%20photography%20warm%20amber%20tones%20cream%20background&width=1600&height=900&seq=home-hero-2&orientation=landscape",
    alt: "職人手作紅豆餅揉麵過程",
  },
  {
    src: "https://readdy.ai/api/search-image?query=variety%20of%20taiyaki%20red%20bean%20cakes%20different%20flavors%20displayed%20on%20wooden%20board%20red%20bean%20taro%20cream%20sweet%20potato%20colorful%20assortment%20warm%20bakery%20atmosphere%20artisan%20handmade%20Taiwanese%20street%20snack%20food%20photography%20warm%20golden%20lighting%20appetizing&width=1600&height=900&seq=home-hero-3&orientation=landscape",
    alt: "多種口味紅豆餅豐富展示",
  },
];

const ROTATE_INTERVAL = 5000;
const FADE_DURATION = 1500;

export default function HomeHero() {
  const { hero } = homePage;
  const [activeIdx, setActiveIdx] = useState(0);

  const nextSlide = useCallback(() => {
    setActiveIdx((prev) => (prev + 1) % heroImages.length);
  }, []);

  useEffect(() => {
    const timer = setInterval(nextSlide, ROTATE_INTERVAL);
    return () => clearInterval(timer);
  }, [nextSlide]);

  return (
    <section className="relative min-h-[520px] md:min-h-[600px] flex items-center justify-center pt-16 md:pt-20 overflow-hidden">
      {/* Background carousel */}
      <div className="absolute inset-0 bg-redbean-primary/40">
        {heroImages.map((img, idx) => (
          <img
            key={idx}
            src={img.src}
            alt={img.alt}
            className="absolute inset-0 w-full h-full object-cover object-center will-change-[opacity]"
            style={{
              opacity: idx === activeIdx ? 1 : 0,
              transition: `opacity ${FADE_DURATION}ms ease-in-out`,
              zIndex: idx === activeIdx ? 1 : 0,
            }}
            loading={idx === 0 ? "eager" : "lazy"}
            decoding="async"
            fetchPriority={idx === 0 ? "high" : "low"}
          />
        ))}
        {/* Dark overlay for text readability */}
        <div className="absolute inset-0 bg-gradient-to-b from-redbean-coffee/60 via-redbean-coffee/50 to-redbean-coffee/70 z-[2]" />
      </div>

      {/* Content */}
      <div className="relative z-10 mx-auto px-4 md:px-6 lg:px-8 text-center" style={{ maxWidth: "900px" }}>
        <span className="inline-block px-4 py-1.5 bg-redbean-gold text-redbean-coffee text-sm font-semibold rounded-full mb-6">
          {hero.tag}
        </span>
        <h1
          className="font-heading font-black text-redbean-cream mb-6 text-balance"
          style={{ fontSize: "clamp(2rem, 5vw, 2.75rem)", lineHeight: 1.25 }}
        >
          {hero.title}
        </h1>
        <p
          className="text-redbean-cream/85 mb-8 max-w-2xl mx-auto"
          style={{ fontSize: "1.1875rem", lineHeight: 1.75 }}
        >
          {hero.subtitle}
        </p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <Link
            to="/products"
            className="inline-flex items-center justify-center gap-2 px-7 py-3.5 bg-redbean-primary text-redbean-cream font-semibold rounded-full hover:bg-redbean-coffee transition-colors whitespace-nowrap text-[1.0625rem]"
          >
            <span className="w-5 h-5 flex items-center justify-center">
              <i className="ri-cake-3-line" />
            </span>
            {hero.ctaPrimary}
          </Link>
          <Link
            to="/franchise"
            className="inline-flex items-center justify-center gap-2 px-7 py-3.5 border-2 border-redbean-cream text-redbean-cream font-semibold rounded-full hover:bg-redbean-cream hover:text-redbean-coffee transition-colors whitespace-nowrap text-[1.0625rem]"
          >
            <span className="w-5 h-5 flex items-center justify-center">
              <i className="ri-store-3-line" />
            </span>
            {hero.ctaSecondary}
          </Link>
        </div>
      </div>

      {/* Slide indicators */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 flex gap-2">
        {heroImages.map((_, idx) => (
          <button
            key={idx}
            type="button"
            onClick={() => setActiveIdx(idx)}
            className={`w-2.5 h-2.5 rounded-full transition-all duration-300 cursor-pointer ${
              idx === activeIdx
                ? "bg-redbean-gold w-8"
                : "bg-redbean-cream/40 hover:bg-redbean-cream/60"
            }`}
            aria-label={`切換背景圖片 ${idx + 1}`}
          />
        ))}
      </div>
    </section>
  );
}